import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Ban, CheckCircle2, KeyRound, Plus, Power, RefreshCw } from 'lucide-react';

import { AppToast } from '../components/AppToast';
import { appConfig } from '../config/appConfig';
import {
  apiKeyService,
  type DeveloperApiKeyRecord,
} from '../services/apiKeyService';

function formatTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function isKeyActive(status: string): boolean {
  return status === 'active';
}

type DialogState =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'reveal'; apiKey: string; heading: string };

export function ApiKeysPage() {
  const [keys, setKeys] = useState<DeveloperApiKeyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [name, setName] = useState('default');
  const [creating, setCreating] = useState(false);
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });
  const [busyKeyId, setBusyKeyId] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const showToast = useCallback((message: string) => {
    setToast(message);
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 2800);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiKeyService.listKeys();
      setKeys(Array.isArray(data.keys) ? data.keys : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
      setKeys([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  function openCreateDialog() {
    setName('default');
    setDialogError(null);
    setCopyState('idle');
    setDialog({ kind: 'create' });
  }

  function closeDialog() {
    setDialog({ kind: 'closed' });
    setDialogError(null);
    setCopyState('idle');
    setCreating(false);
  }

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setCreating(true);
    setDialogError(null);
    setCopyState('idle');
    try {
      const created = await apiKeyService.createKey({ name });
      setDialog({
        kind: 'reveal',
        apiKey: created.secret,
        heading: '请立即保存 API Key（仅显示一次）',
      });
      setName('default');
      showToast(`已创建「${created.key.name}」`);
      await reload();
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : '创建失败');
    } finally {
      setCreating(false);
    }
  }

  async function handleDisable(keyId: string, keyName: string) {
    if (!window.confirm(`确定暂时禁用「${keyName}」？之后可再启用。`)) return;
    setBusyKeyId(keyId);
    setError(null);
    try {
      await apiKeyService.disableKey(keyId);
      showToast(`已禁用「${keyName}」`);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : '禁用失败');
    } finally {
      setBusyKeyId(null);
    }
  }

  async function handleEnable(keyId: string, keyName: string) {
    setBusyKeyId(keyId);
    setError(null);
    try {
      await apiKeyService.enableKey(keyId);
      showToast(`已启用「${keyName}」`);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : '启用失败');
    } finally {
      setBusyKeyId(null);
    }
  }

  async function handleDelete(keyId: string, keyName: string) {
    if (!window.confirm(`确定永久删除「${keyName}」？此操作不可恢复。`)) return;
    setBusyKeyId(keyId);
    setError(null);
    try {
      await apiKeyService.deleteKeyPermanently(keyId);
      showToast(`已删除「${keyName}」`);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除失败');
    } finally {
      setBusyKeyId(null);
    }
  }

  async function handleRotate(keyId: string, keyName: string) {
    if (!window.confirm(`确定轮换「${keyName}」？旧 API Key 将立即失效，新 API Key 只显示一次。`)) return;
    setBusyKeyId(keyId);
    setError(null);
    setDialogError(null);
    setCopyState('idle');
    try {
      const rotated = await apiKeyService.rotateKey(keyId);
      setDialog({
        kind: 'reveal',
        apiKey: rotated.secret,
        heading: '请立即保存新的 API Key（仅显示一次）',
      });
      showToast(`已轮换「${keyName}」`);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : '轮换失败');
    } finally {
      setBusyKeyId(null);
    }
  }

  async function copyApiKey() {
    if (dialog.kind !== 'reveal') return;
    try {
      await navigator.clipboard.writeText(dialog.apiKey);
      setCopyState('copied');
      showToast('已复制到剪贴板');
      window.setTimeout(() => setCopyState((prev) => (prev === 'copied' ? 'idle' : prev)), 2500);
    } catch {
      setCopyState('failed');
      setDialogError('复制失败，请手动选中复制');
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Link to="/profile" className="text-sm text-primary">← 账户</Link>
      <div>
        <h1 className="text-2xl font-semibold text-ink">API 密钥</h1>
        <p className="mt-1 text-sm text-muted">
          供 Agent / Skills 调用 OpenAPI Gateway。完整 API Key 仅在创建或轮换时显示一次。
        </p>
        <p className="mt-1 font-mono text-xs text-muted">
          Gateway: {appConfig.openapiGatewayBaseUrl}
        </p>
      </div>

      {error ? <p className="text-sm text-danger">{error}</p> : null}

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-medium text-ink">已有密钥</h2>
            <button
              type="button"
              onClick={() => void reload()}
              className="inline-flex items-center gap-1 text-xs text-muted transition hover:text-ink"
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden />
              刷新
            </button>
          </div>
          <button
            type="button"
            onClick={openCreateDialog}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white"
          >
            <Plus className="h-4 w-4" aria-hidden />
            新建密钥
          </button>
        </div>
        {loading ? <p className="text-sm text-muted">加载中…</p> : null}
        {!loading && keys.length === 0 ? (
          <p className="text-sm text-muted">暂无 API Key。点击右上角「新建密钥」开始。</p>
        ) : null}
        <ul className="space-y-3">
          {keys.map((key) => {
            const active = isKeyActive(key.status);
            return (
              <li
                key={key.id}
                className={[
                  'rounded-xl border p-4 text-sm shadow-card transition-colors',
                  active
                    ? 'border-emerald-200 bg-emerald-50/40'
                    : 'border-amber-200 bg-amber-50/50 opacity-95',
                ].join(' ')}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <KeyRound
                        className={active ? 'h-4 w-4 shrink-0 text-emerald-700' : 'h-4 w-4 shrink-0 text-amber-700'}
                        aria-hidden
                      />
                      <p className="font-medium text-ink">{key.name}</p>
                      <span
                        className={[
                          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                          active
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-amber-100 text-amber-900',
                        ].join(' ')}
                      >
                        {active ? (
                          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                        ) : (
                          <Ban className="h-3.5 w-3.5" aria-hidden />
                        )}
                        {active ? '启用中' : '已禁用'}
                      </span>
                    </div>
                    <p className="font-mono text-xs text-muted">{key.keyPrefix}…</p>
                    <p className="text-xs text-muted">
                      scopes {(key.scopes || []).join(', ') || '—'}
                    </p>
                    <p className="text-xs text-muted">创建 {formatTime(key.createdAt)}</p>
                    <p className="text-xs text-muted">最近使用 {formatTime(key.lastUsedAt)}</p>
                    {!active && key.revokedAt ? (
                      <p className="text-xs text-amber-800/80">禁用于 {formatTime(key.revokedAt)}</p>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {active ? (
                      <>
                        <button
                          type="button"
                          disabled={busyKeyId === key.id}
                          onClick={() => void handleRotate(key.id, key.name)}
                          className="rounded-lg border border-border bg-white px-3 py-1.5 disabled:opacity-60"
                        >
                          轮换
                        </button>
                        <button
                          type="button"
                          disabled={busyKeyId === key.id}
                          onClick={() => void handleDisable(key.id, key.name)}
                          className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-amber-900 disabled:opacity-60"
                        >
                          <Ban className="h-3.5 w-3.5" aria-hidden />
                          禁用
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        disabled={busyKeyId === key.id}
                        onClick={() => void handleEnable(key.id, key.name)}
                        className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-emerald-800 disabled:opacity-60"
                      >
                        <Power className="h-3.5 w-3.5" aria-hidden />
                        启用
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={busyKeyId === key.id}
                      onClick={() => void handleDelete(key.id, key.name)}
                      className="rounded-lg border border-danger/30 bg-white px-3 py-1.5 text-danger disabled:opacity-60"
                    >
                      删除
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {dialog.kind !== 'closed' ? (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/45 p-4"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && dialog.kind === 'create') closeDialog();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="api-key-dialog-title"
            className="w-full max-w-md rounded-2xl bg-surface p-5 shadow-card"
          >
            {dialog.kind === 'create' ? (
              <>
                <h3 id="api-key-dialog-title" className="text-lg font-semibold text-ink">
                  新建 API Key
                </h3>
                <p className="mt-1 text-sm text-muted">
                  创建后完整 API Key 只显示一次，请妥善保存。
                </p>
                <form onSubmit={handleCreate} className="mt-4 space-y-3">
                  <label className="block text-sm">
                    <span className="text-muted">名称</span>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-border px-3 py-2"
                      placeholder="例如：cursor-agent"
                      autoFocus
                    />
                  </label>
                  <p className="text-xs text-muted">默认 scopes：organize, review, read</p>
                  {dialogError ? <p className="text-sm text-danger">{dialogError}</p> : null}
                  <div className="flex justify-end gap-2 pt-1">
                    <button
                      type="button"
                      disabled={creating}
                      onClick={closeDialog}
                      className="rounded-lg border border-border px-4 py-2 text-sm"
                    >
                      取消
                    </button>
                    <button
                      type="submit"
                      disabled={creating || !name.trim()}
                      className="rounded-lg bg-primary px-4 py-2 text-sm text-white disabled:opacity-60"
                    >
                      {creating ? '创建中…' : '创建'}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <h3 id="api-key-dialog-title" className="text-lg font-semibold text-ink">
                  {dialog.heading}
                </h3>
                <p className="mt-1 text-sm text-muted">
                  关闭后将无法再次查看完整 API Key。
                </p>
                <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3">
                  <p className="text-xs font-medium text-amber-900">API Key</p>
                  <code className="mt-1.5 block break-all rounded-lg bg-white px-3 py-2 font-mono text-xs text-ink">
                    {dialog.apiKey}
                  </code>
                </div>
                {dialogError ? <p className="mt-2 text-sm text-danger">{dialogError}</p> : null}
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => void copyApiKey()}
                    className="rounded-lg bg-primary px-4 py-2 text-sm text-white"
                  >
                    {copyState === 'copied' ? '已复制' : '复制 API Key'}
                  </button>
                  <button
                    type="button"
                    onClick={closeDialog}
                    className="rounded-lg border border-border px-4 py-2 text-sm"
                  >
                    我已保存
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      {toast ? <AppToast message={toast} /> : null}
    </div>
  );
}
